"use client";

import { type Dispatch, type SetStateAction } from "react";
import { type UseFormReturn } from "react-hook-form";
import { AnimatePresence, motion } from "motion/react";
import { type i18n } from "i18next";
import { type TFunction } from "i18next";
import { format } from "date-fns";
import { Icon } from "@iconify-icon/react";

import { TimePicker } from "./time-picker.tsx";

import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Calendar } from "@/components/ui/calendar.tsx";
import { getDateFnsLocale, getDateStringLocale } from "@/i18n/getDateFnsLocale.ts";
import { type CreateSlotFormData, LessonHost } from "@/features/calendar/types/create-slot";
import { type HostingOption } from "@/features/calendar/types/hostingOptions.tsx";
import { type CalendarData } from "@/features/calendar/types/calendar";
import {
  calculateTotalTime,
  formatTotalTime,
  renderHostingOptions,
  slideUpAnimation,
  staggeredContainerAnimation,
} from "@/features/calendar/utils/slot-creation";

interface CustomSlotTabProps {
  form: UseFormReturn<CreateSlotFormData>;
  t: TFunction<("forms" | "common" | "calendar")[], undefined>;
  i18n: i18n;
  calendarData: CalendarData | null;
  hostingOptions: HostingOption[];
  createMultiple: boolean;
  setCreateMultiple: Dispatch<SetStateAction<boolean>>;
  numberOfSlots: number | undefined;
  setNumberOfSlots: Dispatch<SetStateAction<number | undefined>>;
  maxSlots: number;
  breakMinutes: number | undefined;
  setBreakMinutes: Dispatch<SetStateAction<number | undefined>>;
}

export function CustomSlotTab({
  form,
  t,
  i18n,
  calendarData,
  hostingOptions,
  createMultiple,
  setCreateMultiple,
  numberOfSlots,
  setNumberOfSlots,
  maxSlots,
  breakMinutes,
  setBreakMinutes,
}: CustomSlotTabProps) {
  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key="custom-form"
        animate="animate"
        className="space-y-6 p-6"
        exit="exit"
        initial="initial"
        variants={staggeredContainerAnimation}
      >
        {/* Date and Time Section */}
        <motion.div variants={slideUpAnimation}>
          <Card className="bg-muted/20 relative z-50 rounded-2xl border-0 shadow-2xl backdrop-blur-xl">
            <div className={"absolute inset-0 overflow-hidden rounded-2xl"}>
              <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-500/15 to-emerald-600/15 blur-2xl" />
            </div>
            <CardContent className="relative z-50 p-5">
              <div className="mb-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className={"space-y-0"}>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("common:date")}
                      </FormLabel>
                      <FormControl>
                        <Popover modal={true}>
                          <PopoverTrigger asChild>
                            <Button
                              className="bg-muted hover:bg-side-accent group flex h-11 w-full items-center justify-between rounded-md border-0 px-3 text-left transition-colors"
                              variant="outline"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex size-8 items-center justify-center rounded-md">
                                  <Icon
                                    className="text-accent-foreground"
                                    height="1.25rem"
                                    icon="tabler:calendar"
                                    width="1.25rem"
                                  />
                                </div>
                                <span className="text-accent-foreground">
                                  {new Date(field.value).toLocaleDateString(
                                    getDateStringLocale(i18n.language),
                                  )}
                                </span>
                              </div>
                              <Icon
                                className="text-muted-foreground"
                                height="1rem"
                                icon="tabler:chevron-down"
                                width="1rem"
                              />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="z-[1502]! w-auto p-0">
                            <Calendar
                              initialFocus
                              fromDate={new Date()}
                              locale={getDateFnsLocale(i18n.language)}
                              mode="single"
                              selected={new Date(field.value)}
                              toDate={new Date(calendarData?.calendarEndsAt ?? new Date())}
                              weekStartsOn={1}
                              onSelect={(val) =>
                                field.onChange(format(val ?? new Date(), "yyyy-MM-dd"))
                              }
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage className="mt-1" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("calendar:templates.startTime")}
                      </FormLabel>
                      <FormControl>
                        <div className="h-11">
                          <TimePicker
                            className="bg-muted"
                            pickedDate={new Date(form.watch("date"))}
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <div className="bg-muted hover:bg-side-accent flex h-11 cursor-pointer items-center gap-2 rounded-md border-0 px-4 transition-colors">
                              <Icon
                                className="text-muted-foreground"
                                height={"1.25rem"}
                                icon="tabler:clock"
                                width={"1.25rem"}
                              />
                              <span className="text-accent-foreground text-base">
                                {field.value}
                              </span>
                            </div>
                          </TimePicker>
                        </div>
                      </FormControl>
                      <FormMessage className="mt-1" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("calendar:templates.endTime")}
                      </FormLabel>
                      <FormControl>
                        <div className="h-11">
                          <TimePicker
                            className="bg-muted"
                            pickedDate={new Date(form.watch("date"))}
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <div className="bg-muted hover:bg-side-accent flex h-11 cursor-pointer items-center gap-2 rounded-md border-0 px-4 transition-colors">
                              <Icon
                                className="text-muted-foreground"
                                height={"1.25rem"}
                                icon="tabler:clock"
                                width={"1.25rem"}
                              />
                              <span className="text-accent-foreground text-base">
                                {field.value}
                              </span>
                            </div>
                          </TimePicker>
                        </div>
                      </FormControl>
                      <FormMessage className="mt-1" />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Hosting Method Section */}
        <motion.div variants={slideUpAnimation}>
          <Card className="bg-muted/20 relative rounded-2xl border-0 shadow-2xl backdrop-blur-xl">
            <div className={"absolute inset-0 overflow-hidden rounded-2xl"}>
              <div className="absolute -top-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-600/20 blur-3xl" />
              <div className="absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-gradient-to-tl from-orange-500/15 to-red-600/15 blur-2xl" />
            </div>
            <CardContent className="relative z-50 p-5">
              <FormField
                control={form.control}
                name="lesson_host"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("calendar:templates.lessonHost")}
                      </FormLabel>
                      <RadioGroup
                        className="flex flex-col space-y-3"
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {renderHostingOptions(hostingOptions, field.value, field.onChange, t)}
                      </RadioGroup>
                      <FormMessage className="mt-1" />
                    </FormItem>
                  );
                }}
              />

              {/* Communication Details (only if other_way is selected) */}
              <AnimatePresence>
                {form.watch("lesson_host") === LessonHost.OtherWay && (
                  <motion.div
                    key="communication-details"
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4"
                    exit={{ opacity: 0, height: 0 }}
                    initial={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FormField
                      control={form.control}
                      name="communication_details"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground mb-2 block text-sm">
                            {t("forms:hostMethodSelector.communicationDetails")}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              className={`bg-muted hover:bg-side-accent/70 min-h-[80px] resize-none border-0 text-base transition-colors ${
                                form.formState.errors.communication_details
                                  ? "border-red-500 ring-red-500"
                                  : ""
                              }`}
                              placeholder={t(
                                "forms:hostMethodSelector.communicationDetailsPlaceholder",
                              )}
                              {...field}
                              maxLength={255}
                            />
                          </FormControl>
                          <FormMessage className="mt-1" />
                        </FormItem>
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        {/* Creation Options for Custom Tab */}
        <motion.div variants={slideUpAnimation}>
          <Card className="bg-muted/20 relative rounded-2xl border-0 shadow-2xl backdrop-blur-xl">
            <div className={"absolute inset-0 overflow-hidden rounded-2xl"}>
              <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-bl from-cyan-500/20 to-blue-600/20 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-pink-500/15 to-rose-600/15 blur-2xl" />
            </div>
            <CardContent className="relative z-50 p-5">
              <div className="mb-4">
                <h3 className="text-accent-foreground mb-4 text-lg font-medium">
                  {t("calendar:templates.createOptions")}
                </h3>

                <RadioGroup
                  className="flex flex-col space-y-3"
                  value={createMultiple ? "multiple" : "single"}
                  onValueChange={(value) => {
                    setCreateMultiple(value === "multiple");
                  }}
                >
                  <div
                    className={`cursor-pointer rounded-md p-4 transition-all ${
                      !createMultiple ? "bg-muted shadow-md" : "bg-muted/50 hover:bg-side-accent"
                    }`}
                  >
                    <FormItem className="m-0 flex items-center space-y-0 space-x-3">
                      <FormControl>
                        <RadioGroupItem
                          className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground"
                          value="single"
                        />
                      </FormControl>
                      <FormLabel className="m-0 flex w-full cursor-pointer items-center justify-between text-start font-normal">
                        <div className="flex items-center gap-3">
                          <div className="bg-muted flex size-10 items-center justify-center rounded-lg p-2">
                            <Icon
                              height={"1.25rem"}
                              icon="tabler:calendar-plus"
                              width={"1.25rem"}
                            />
                          </div>
                          <div>
                            <span className="text-accent-foreground text-base font-medium">
                              {t("calendar:templates.singleSlot")}
                            </span>
                            <p className="text-muted-foreground mt-1 text-sm">
                              {t("calendar:templates.createSingleSlot")}
                            </p>
                          </div>
                        </div>
                      </FormLabel>
                    </FormItem>
                  </div>

                  <div
                    className={`cursor-pointer rounded-md p-4 transition-all ${
                      createMultiple ? "bg-muted shadow-md" : "bg-muted/50 hover:bg-side-accent"
                    }`}
                  >
                    <FormItem className="m-0 flex items-center space-y-0 space-x-3">
                      <FormControl>
                        <RadioGroupItem
                          className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground"
                          value="multiple"
                        />
                      </FormControl>
                      <FormLabel className="m-0 flex w-full cursor-pointer items-center justify-between text-start font-normal">
                        <div className="flex items-center gap-3">
                          <div className="bg-muted flex size-10 items-center justify-center rounded-lg p-2">
                            <Icon
                              height={"1.25rem"}
                              icon="tabler:calendar-repeat"
                              width={"1.25rem"}
                            />
                          </div>
                          <div>
                            <span className="text-accent-foreground text-base font-medium">
                              {t("calendar:templates.multipleSlots")}
                            </span>
                            <p className="text-muted-foreground mt-1 text-sm">
                              {t("calendar:templates.createMultipleSlots")}
                            </p>
                          </div>
                        </div>
                      </FormLabel>
                    </FormItem>
                  </div>
                </RadioGroup>
              </div>

              {createMultiple && (
                <motion.div
                  key="multiple-options"
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 space-y-4"
                  exit={{ opacity: 0, height: 0 }}
                  initial={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <FormItem>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("calendar:templates.numberOfSlots")}
                      </FormLabel>
                      <div className="group relative">
                        <div className="bg-muted group-hover:bg-side-accent group-focus-within:bg-side-accent absolute top-0 left-0 flex h-full w-14 items-center justify-center rounded-l-md transition-colors">
                          <Icon
                            className="text-muted-foreground"
                            height={"1.25rem"}
                            icon="tabler:repeat"
                            width={"1.25rem"}
                          />
                        </div>
                        <Input
                          className="bg-muted hover:bg-side-accent group-hover:bg-side-accent focus:bg-side-accent h-11 appearance-none border-0 pl-14 text-base font-medium transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          max={maxSlots}
                          min="1"
                          placeholder={t("calendar:templates.maxSlots", {
                            count: maxSlots,
                          })}
                          type="number"
                          value={numberOfSlots === undefined ? "" : numberOfSlots}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? undefined
                                : Math.min(
                                    Math.max(1, Number.parseInt(e.target.value) || 1),
                                    maxSlots,
                                  );

                            setNumberOfSlots(value);
                          }}
                        />
                      </div>
                    </FormItem>

                    <FormItem>
                      <FormLabel className="text-muted-foreground mb-2 block text-sm">
                        {t("calendar:templates.breakBetweenSlots")}
                      </FormLabel>
                      <div className="group relative">
                        <div className="bg-muted group-hover:bg-side-accent group-focus-within:bg-side-accent absolute top-0 left-0 flex h-full w-14 items-center justify-center rounded-l-md transition-colors">
                          <Icon
                            className="text-muted-foreground"
                            height={"1.25rem"}
                            icon="tabler:clock-pause"
                            width={"1.25rem"}
                          />
                        </div>
                        <Input
                          className="bg-muted hover:bg-side-accent group-hover:bg-side-accent focus:bg-side-accent h-11 appearance-none border-0 pl-14 text-base font-medium transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          max={999}
                          maxLength={3}
                          min="0"
                          placeholder={t("calendar:templates.breakInMinutes")}
                          type="number"
                          value={breakMinutes === undefined ? "" : breakMinutes}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? undefined
                                : Math.max(0, Number.parseInt(e.target.value) || 0);

                            if (value && value > 180) return;

                            setBreakMinutes(value);
                          }}
                        />
                      </div>
                    </FormItem>
                  </div>

                  <div className="bg-muted/50 mt-2 rounded-md p-3">
                    <div className="flex items-center">
                      <Icon
                        className="text-muted-foreground mr-2"
                        height={"1rem"}
                        icon="tabler:info-circle"
                        width={"1rem"}
                      />
                      <p className="text-muted-foreground text-sm">
                        {t("calendar:templates.slotsPreview", {
                          count: numberOfSlots ?? 0,
                          totalTime: formatTotalTime(
                            calculateTotalTime(
                              form.watch("start_time"),
                              form.watch("end_time"),
                              numberOfSlots,
                              breakMinutes,
                            ),
                            t,
                          ),
                        })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
